import React from 'react';
import {
  WithStyles,
  withStyles,
  createStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import SurveyItem from '../../util/SurveyItem';
import {
  withSurveyContext,
  withIsReadOnly,
  WithIsReadOnlyInjectedProps,
  WithSurveyContextInjectedProps,
} from '../Contexts';
import SurveyField from '../SurveyField';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../hoc/withCheckForEnablement';
import withValidation, {
  WithValidationInjectedProps,
} from '../hoc/withValidation';
import ChoiceOptions from '../../util/ChoiceOptions';
import { TableRow, TableCell } from '@mui/material';
import { FieldProps } from 'formik';
import getSurveyItemTestId from '../../util/getSurveyItemTestId';
import { getTestProps } from '../../../../util/testProps';
import { Radio } from '@lifeomic/chroma-react/components/Radio';
import { Text } from '@lifeomic/chroma-react/components/Text';

export const testIds = {
  tableRow: (linkId: string) => getSurveyItemTestId(linkId, 'row'),
  rowError: (linkId: string) => getSurveyItemTestId(linkId, 'row-error'),
  rowItem: (linkId: string, value: string) =>
    getSurveyItemTestId(linkId, value),
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%', // TODO: this is a placeholder, should be responsive
    },
    questionCell: {
      display: 'flex',
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
    cellBody: {
      padding: theme.spacing(0.75, 1),
    },
    radioButton: {
      display: 'block',
      marginTop: theme.spacing(2.5),
    },
    labelContainer: {
      display: 'none',
    },
    errorText: {
      color: theme.palette.error.main,
    },
  });

interface FieldComponentOwnProps {
  surveyItem: SurveyItem;
  shouldShowError: boolean;
  error: string;
  choiceOptions: ChoiceOptions;
  isReadOnly: boolean;
}

type FieldComponentProps = FieldComponentOwnProps &
  FieldProps<any> &
  WithStyles<typeof styles>;

class FieldComponent extends React.PureComponent<FieldComponentProps> {
  // The field value may be represented as a complex object in form state, but must be represented as a simple value for the control
  wrapValue = (value: any) => {
    return this.props.choiceOptions.formToResponseValue(value);
  };

  unwrapValue = (responseValue: any) => {
    return this.props.choiceOptions.responseToFormValue(responseValue);
  };

  handleToggle = (event: any) => {
    const { form, field } = this.props;

    const targetValue = event?.target?.value;
    if (!isNil(targetValue)) {
      const unwrappedCurrentValue = this.unwrapValue(this.props.field.value);
      if (isEqual(targetValue, unwrappedCurrentValue)) {
        // toggle off
        form.setFieldValue(field.name, null);
      } else {
        // toggle on
        const wrappedValue = this.wrapValue(targetValue);
        form.setFieldValue(field.name, wrappedValue);
      }
    }
  };

  render() {
    const {
      field: { value },
      surveyItem,
      classes,
      shouldShowError,
      error,
      choiceOptions,
      isReadOnly,
    } = this.props;

    const selectValue = this.unwrapValue(value);

    return (
      <TableRow {...getTestProps(testIds.tableRow(surveyItem.link))}>
        <TableCell classes={{ root: classes.cellBody }}>
          <Text
            size="body"
            className={shouldShowError ? classes.errorText : undefined}
          >
            {surveyItem.label}
          </Text>
          {shouldShowError ? (
            <Text
              size="caption"
              className={shouldShowError ? classes.errorText : undefined}
              {...getTestProps(testIds.rowError(surveyItem.link))}
            >
              {error}
            </Text>
          ) : null}
        </TableCell>
        {choiceOptions.options.map((option, index) => {
          const optionValue = this.unwrapValue(option.value);
          const checked = optionValue === selectValue;
          return (
            <TableCell
              key={index}
              classes={{ root: classes.cellBody }}
              size="small"
              align="center"
            >
              <Radio
                key={`${surveyItem.link}-${index}-${checked}`}
                label=""
                aria-label={option.display}
                className={classes.radioButton}
                checked={optionValue === selectValue}
                value={optionValue}
                onClick={this.handleToggle}
                disabled={isReadOnly}
                {...getTestProps(testIds.rowItem(surveyItem.link, optionValue))}
              />
            </TableCell>
          );
        })}
      </TableRow>
    );
  }
}

interface ChoiceOwnProps {
  choiceOptions: ChoiceOptions;
  surveyItem: SurveyItem;
}

export type ChoiceProps = ChoiceOwnProps &
  WithStyles<typeof styles> &
  WithCheckForEnablementInjectedProps &
  WithIsReadOnlyInjectedProps &
  WithSurveyContextInjectedProps &
  WithValidationInjectedProps;

class Choice extends React.PureComponent<ChoiceProps> {
  render() {
    const {
      surveyItem,
      isEnabled,
      shouldShowError,
      error,
      validate,
      choiceOptions,
      classes,
      isReadOnly,
    } = this.props;

    if (!isEnabled) return null;

    return (
      <SurveyField
        validate={validate}
        responseAnswerType={surveyItem.responseValueType}
        name={surveyItem.link}
        component={FieldComponent}
        surveyItem={surveyItem}
        isEnabled={isEnabled}
        isReadOnly={isReadOnly}
        error={error}
        shouldShowError={shouldShowError}
        choiceOptions={choiceOptions}
        classes={classes}
      />
    );
  }
}

export default withStyles(styles)(
  withIsReadOnly(
    withCheckForEnablement(withValidation(withSurveyContext(Choice))),
  ),
);
